import i18n from "i18next";
import dayjs from "dayjs";
import {
    EventCategoryStatistic,
    EventDrinkStatistic,
    EventTimelineBucket,
    EventTopConsumer,
} from "../../../services/drinkService";

export const formatNumber = (value: number, digits = 0): string => value.toLocaleString(i18n.language ?? "en", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const formatVolume = (value: number): string => `${formatNumber(Math.round(value))} ml`;

export const formatAlcohol = (value: number): string => `${formatNumber(value, 1)} g`;

type TimelineLine = { key: string; name: string; userId: number };

type TimelineSeries = {
    volume: { data: Record<string, any>[]; lines: TimelineLine[] };
    alcohol: { data: Record<string, any>[]; lines: TimelineLine[] };
};

export const buildTimelineSeries = (timeline: EventTimelineBucket[], visibleUsers?: Set<number>): TimelineSeries => {
    if (!timeline.length) {
        return {
            volume: { data: [], lines: [] },
            alcohol: { data: [], lines: [] },
        };
    }

    const userLines = new Map<number, TimelineLine>();

    timeline.forEach((bucket) => {
        bucket.users.forEach((user) => {
            if (visibleUsers && !visibleUsers.has(user.userId)) {
                return;
            }

            if (!userLines.has(user.userId)) {
                userLines.set(user.userId, {
                    key: `user-${user.userId}`,
                    name: user.username,
                    userId: user.userId,
                });
            }
        });
    });

    const lines = Array.from(userLines.values());
    const data = { volume: [] as Record<string, any>[], alcohol: [] as Record<string, any>[] };
    if (lines.length > 0) {
        timeline.forEach((bucket) => {
            const bucketLabel = dayjs(bucket.bucketStart).format("MMM D HH:mm");
            const bucketUserMap = new Map<number, EventTimelineBucket["users"][number]>();
            bucket.users.forEach((user) => {
                if (!visibleUsers || visibleUsers.has(user.userId)) {
                    bucketUserMap.set(user.userId, user);
                }
            });
            const volume: Record<string, any> = { bucket: bucketLabel };
            const alcohol: Record<string, any> = { bucket: bucketLabel };
            lines.forEach((line) => {
                const userData = bucketUserMap.get(line.userId);
                volume[line.key] = userData ? Number(userData.totalVolumeMl.toFixed(2)) : 0;
                alcohol[line.key] = userData ? Number(userData.totalAlcoholGrams.toFixed(2)) : 0;
            });
            data.volume.push(volume);
            data.alcohol.push(alcohol);
        });
    }
    return { volume: { data: data.volume, lines }, alcohol: { data: data.alcohol, lines } };
};

export const sortParticipantsDesc = (participants: EventTopConsumer[]) =>
    [...participants].sort(
        (a, b) =>
            b.totalVolumeMl - a.totalVolumeMl ||
            b.totalAlcoholGrams - a.totalAlcoholGrams ||
            a.userId - b.userId
    );

export const filterVisibleParticipants = (
    participants: EventTopConsumer[],
    showAll: boolean,
    visibleUserIds: Set<number>
): EventTopConsumer[] => {
    if (showAll) return participants;
    if (!visibleUserIds.size) return [];
    return participants.filter((participant) => visibleUserIds.has(participant.userId));
};

export const selectDrinkStats = (selectedDrinkId: number | null, drinks: EventDrinkStatistic[]) => {
    if (!selectedDrinkId) return null;
    const drink = drinks.find((entry) => entry.drinkId === selectedDrinkId);
    if (!drink) return null;
    return { drink, participantMap: new Map(drink.participants.map((participant) => [participant.userId, participant])) };
};

type TopEntry = {
    label: string;
    totalVolumeMl: number;
    totalAlcoholGrams: number;
    participants: EventTopConsumer[];
    matchedByKeyword: boolean;
};

export const selectTopEntry = (
    drinks: EventDrinkStatistic[],
    categories: EventCategoryStatistic[],
    matcher: (name: string) => boolean,
    matchedByKeyword: boolean
): TopEntry | null => {
    const sortByTotals = <T extends { totalVolumeMl: number; totalAlcoholGrams: number }>(items: T[]) =>
        [...items].sort((a, b) => b.totalVolumeMl - a.totalVolumeMl || b.totalAlcoholGrams - a.totalAlcoholGrams);

    const matchedDrinks = sortByTotals(
        drinks.filter((drink) => matcher(drink.drinkName.toLowerCase()))
    );
    if (matchedDrinks.length > 0) {
        const topDrink = matchedDrinks[0];
        return {
            label: topDrink.drinkName,
            totalVolumeMl: topDrink.totalVolumeMl,
            totalAlcoholGrams: topDrink.totalAlcoholGrams,
            participants: sortParticipantsDesc(topDrink.participants),
            matchedByKeyword,
        };
    }

    const matchedCategories = sortByTotals(
        categories.filter((category) => matcher(category.category.toLowerCase()))
    );
    if (matchedCategories.length > 0) {
        const topCategory = matchedCategories[0];
        return {
            label: topCategory.category,
            totalVolumeMl: topCategory.totalVolumeMl,
            totalAlcoholGrams: topCategory.totalAlcoholGrams,
            participants: sortParticipantsDesc(topCategory.participants),
            matchedByKeyword,
        };
    }

    return null;
};
